import React, { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import { Pagination } from "antd";
import { jsPDF } from 'jspdf';
import { applyPlugin } from 'jspdf-autotable';
import html2canvas from 'html2canvas';

import Logo from "../../assets/logo.png";

import OccupancyRateChart from "../../components/LandLord/OccupancyRateChart";
import TableHeader from "../../components/Common/TableHeader";
import LegendTable from "../../components/Common/LegendTable";
import Table from "../../components/Common/Table";
import InfoCard from "../../components/Common/InfoCard";

import { fetchOccupancyInsights } from "../../store/thunks/analyticsThunks";
import { formatDateToISO } from "../../utils/dateFormating";
import { svgToCanvas } from "../../utils/svgToCanvas";
import showToast from "../../utils/toastNotifications";

const ITEMS_PER_PAGE = 10;
const CHART_COLORS = { occupied: "#3B82F6", vacant: "#10B981" };

const OccupancyRate = () => {
  const dispatch = useDispatch();
  const { data, isLoading } = useSelector((state) => state.analytics);

  const allOccupancyData = data?.propertyData || [];

  const [isPdfGenerating, setIsPdfGenerating] = useState(false);
  const [filters, setFilters] = useState({
    propertyId: "",
    startDate: "",
    endDate: "",
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [searchParams, setSearchParams] = useSearchParams();

  const isFetchingRef = useRef(false);
  const chartRef = useRef(null);
  const legendRef = useRef(null);

  const dropdownOptions = useMemo(() => {

    if (!allOccupancyData.length) {
      return [
        {
          label: "🚫 No property",
          value: "none",
          disabled: true,
        },
      ];
    }

    return allOccupancyData.map((item) => ({
      label: item.propertyName,
      value: item._id,
    }));
  }, [allOccupancyData]);

  useEffect(() => {
    const propertyName = searchParams.get("propertyName") || "";
    const startDate = searchParams.get("startDate");
    const endDate = searchParams.get("endDate");

    const propertyId = allOccupancyData.find(
      (property) => property.propertyName === propertyName
    )?._id;

    setFilters({
      propertyId: propertyId || "",
      startDate: startDate && startDate !== "null" ? startDate : "",
      endDate: endDate && endDate !== "null" ? endDate : "",
    });
    setCurrentPage(Number(searchParams.get("page")) || 1);
  }, [searchParams]);

  const fetchData = useCallback(() => {
    if (isFetchingRef.current) return;
    isFetchingRef.current = true;

    const query = {
      propertyId: filters.propertyId,
      startDate: formatDateToISO(filters.startDate, false),
      endDate: formatDateToISO(filters.endDate, true),
      offset: (currentPage - 1) * ITEMS_PER_PAGE,
      limit: ITEMS_PER_PAGE,
    };
    dispatch(fetchOccupancyInsights(query)).finally(() => {
      isFetchingRef.current = false;
    });
  }, [filters, currentPage, dispatch]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleSearch = () => {
    const queryParams = new URLSearchParams();

    const selectedProperty = allOccupancyData.find(
      (property) => property._id === filters.propertyId
    );

    queryParams.set("propertyName", selectedProperty ? selectedProperty.propertyName : "");

   // queryParams.set("landlordId", filters.landlordId);
   // queryParams.set("propertyId", filters.propertyId);
    queryParams.set("startDate", filters.startDate);
    queryParams.set("endDate", filters.endDate);
    queryParams.set("page", 1);
    setSearchParams(queryParams);
  };

  const handlePageChange = (page) => {
    if (page === currentPage) return;
    const queryParams = new URLSearchParams(searchParams);
    queryParams.set("page", page);
    setSearchParams(queryParams);
  };

  const filteredData = useMemo(() => {
    return filters.propertyId
      ? allOccupancyData.filter((item) => item._id === filters.propertyId)
      : allOccupancyData;
  }, [allOccupancyData, filters.propertyId]);

  const aggregated = useMemo(() => {
    return filteredData.reduce(
      (acc, curr) => {
        acc.rentCollected += parseFloat(curr.rentCollect || "0");
        acc.rentExpected += parseFloat(curr.rentExpected || "0");
        const occupiedUnits = curr.units.filter(
          (unit) => unit.status === "OCCUPIED"
        ).length;
        const vacantUnits = curr.units.filter(
          (unit) => unit.status === "VACANT"
        ).length;
        acc.occupiedUnits += occupiedUnits;
        acc.vacantUnits += vacantUnits;
        acc.totalUnits += curr.numberOfUnits;
        return acc;
      },
      {
        rentCollected: 0,
        rentExpected: 0,
        occupiedUnits: 0,
        vacantUnits: 0,
        totalUnits: 0,
      }
    );
  }, [filteredData]);

  const totalUnits = aggregated.totalUnits;
  const occupiedPercentage =
    totalUnits > 0 ? (aggregated.occupiedUnits / totalUnits) * 100 : 0;
  const vacantPercentage =
    totalUnits > 0 ? (aggregated.vacantUnits / totalUnits) * 100 : 0;

  const legendData = useMemo(
    () => [
      {
        label: "Occupied",
        value: aggregated.occupiedUnits,
        percent: occupiedPercentage,
        color: CHART_COLORS.occupied,
      },
      {
        label: "Vacant",
        value: aggregated.vacantUnits,
        percent: vacantPercentage,
        color: CHART_COLORS.vacant,
      },
    ],
    [aggregated, occupiedPercentage, vacantPercentage]
  );

  const formattedChartData = useMemo(
    () => {

      return [
        {
          category: "Occupied",
          value: aggregated.occupiedUnits,
          color: CHART_COLORS.occupied,
        },
        {
          category: "Vacant",
          value: aggregated.vacantUnits,
          color: CHART_COLORS.vacant,
        },
      ];
    },
    [aggregated]
  );

  const tableData = useMemo(
    () =>
      filteredData.flatMap((property) =>
        (property.units || []).map((unit) => {
          const vacantSince = unit.vacantSince
            ? new Date(unit.vacantSince)
            : null;
          const now = new Date();
          const daysVacant = vacantSince
            ? Math.floor((now - vacantSince) / (1000 * 60 * 60 * 24))
            : 0;

          return {
            unitId: unit.unitId || "-",
            propertyName: property.propertyName,
            vacantDuration: unit.status === "VACANT" ? daysVacant : 0,
          };
        })
      ).sort((a, b) => b.unitId.localeCompare(a.unitId)),
    [filteredData]
  );

  const paginatedTableData = useMemo(
    () =>
      tableData.slice(
        (currentPage - 1) * ITEMS_PER_PAGE,
        currentPage * ITEMS_PER_PAGE
      ),
    [tableData, currentPage]
  );

  const selectedProperty = allOccupancyData.find(
    (p) => p._id === filters.propertyId
  );
  const selectedPropertyName = selectedProperty
    ? selectedProperty.propertyName
    : "All properties";
  
    const propertyCount = filters.propertyId ? 1 : dropdownOptions.length;
    const propertiesCount = filteredData.length;


  const columns = [
    {
      title: "Unit ID",
      dataIndex: "unitId",
      key: "unitId",
    },
    {
      title: "Property Name",
      dataIndex: "propertyName",
      key: "propertyName",
    },
    {
      title: "Days Vacant",
      dataIndex: "vacantDuration",
      key: "vacantDuration",
      render: (days) => (
        <span className={days > 30 ? "text-[#FF0202]" : "text-[#07C01D]"}>
          {days}
        </span>
      ),
    },
  ];

  const createInfoCardSVG = (data) => {
    // Calculate complexity scores for each value
    const rentCollectedStr = data.rentCollected.toFixed(2);
    const rentExpectedStr = data.rentExpected.toFixed(2);
    const propertiesStr = data.propertyCount.toString();
    const unitsStr = data.totalUnits.toString();

    // Determine space needs
    const rentCollectedNeedsSpace = rentCollectedStr.length > 12;
    const rentExpectedNeedsSpace = rentExpectedStr.length > 12;
    const bothRentNeedSpace = rentCollectedNeedsSpace || rentExpectedNeedsSpace;

    // Dynamic width allocation strategy
    let widths;
    if (bothRentNeedSpace) {
      // Prioritize rent cards, minimize property/unit cards
      widths = {
        card1: rentCollectedNeedsSpace ? 250 : 220,
        card2: rentExpectedNeedsSpace ? 250 : 220,
        card3: 100, // Minimal for properties
        card4: 100, // Minimal for units
      };
    } else {
      // Balanced layout for normal values
      widths = {
        card1: 200,
        card2: 200,
        card3: 140,
        card4: 140,
      };
    }

    // Helper function to create text with smart wrapping
    const createTextElement = (value, x, y, fontSize, maxWidth) => {
      const valueStr = value.toString();
      const estimatedWidth = valueStr.length * (fontSize * 0.6);
      
      if (estimatedWidth <= maxWidth) {
        return `
          <text x="${x}" y="${y}" font-family="Inter, sans-serif" font-size="${fontSize}" font-weight="bold" fill="url(#textGradient)">
            ${valueStr}
          </text>
        `;
      } else {
        // Split into two lines
        const commaIndex = valueStr.indexOf(',');
        let firstLine, secondLine;
        
        if (commaIndex !== -1 && commaIndex > 2) {
          firstLine = valueStr.substring(0, commaIndex + 1);
          secondLine = valueStr.substring(commaIndex + 1);
        } else {
          const midPoint = Math.floor(valueStr.length / 2);
          firstLine = valueStr.substring(0, midPoint);
          secondLine = valueStr.substring(midPoint);
        }
        
        const adjustedFontSize = fontSize - 2; // Slightly smaller for wrapped text
        return `
          <text x="${x}" y="${y - 8}" font-family="Inter, sans-serif" font-size="${adjustedFontSize}" font-weight="bold" fill="url(#textGradient)">
            ${firstLine}
          </text>
          <text x="${x}" y="${y + 10}" font-family="Inter, sans-serif" font-size="${adjustedFontSize}" font-weight="bold" fill="url(#textGradient)">
            ${secondLine}
          </text>
        `;
      }
    };

    // Calculate positions
    const positions = {
      card1: 20,
      card2: 20 + widths.card1 + 20,
      card3: 20 + widths.card1 + 20 + widths.card2 + 20,
      card4: 20 + widths.card1 + 20 + widths.card2 + 20 + widths.card3 + 20,
    };

    const totalWidth = positions.card4 + widths.card4 + 20;

    const svgContent = `
      <svg width="${totalWidth}" height="200" xmlns="http://www.w3.org/2000/svg">
        <defs>
          <linearGradient id="textGradient" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" style="stop-color:#4E6CF0;stop-opacity:1" />
            <stop offset="100%" style="stop-color:#8442E3;stop-opacity:1" />
          </linearGradient>
          <filter id="shadow" x="-20%" y="-20%" width="140%" height="140%">
            <feDropShadow dx="2" dy="2" stdDeviation="3" flood-color="rgba(0,0,0,0.1)"/>
          </filter>
        </defs>
        
        <!-- Card 1: Rent Collected -->
        <rect x="${positions.card1}" y="40" width="${widths.card1}" height="100" rx="5" fill="white" filter="url(#shadow)"/>
        <text x="${positions.card1 + 10}" y="65" font-family="Inter, sans-serif" font-size="12" font-weight="bold" fill="url(#textGradient)">
          Rent Collected (UGX)
        </text>
        ${createTextElement(rentCollectedStr, positions.card1 + 10, 100, 20, widths.card1 - 20)}
        
        <!-- Card 2: Rent Expected -->
        <rect x="${positions.card2}" y="40" width="${widths.card2}" height="100" rx="5" fill="white" filter="url(#shadow)"/>
        <text x="${positions.card2 + 10}" y="65" font-family="Inter, sans-serif" font-size="12" font-weight="bold" fill="url(#textGradient)">
          Rent Expected (UGX)
        </text>
        ${createTextElement(rentExpectedStr, positions.card2 + 10, 100, 20, widths.card2 - 20)}
        
        <!-- Card 3: Properties -->
        <rect x="${positions.card3}" y="40" width="${widths.card3}" height="100" rx="5" fill="white" filter="url(#shadow)"/>
        <text x="${positions.card3 + 10}" y="65" font-family="Inter, sans-serif" font-size="12" font-weight="bold" fill="url(#textGradient)">
          Properties
        </text>
        ${createTextElement(propertiesStr, positions.card3 + 10, 105, 36, widths.card3 - 20)}
        
        <!-- Card 4: Units -->
        <rect x="${positions.card4}" y="40" width="${widths.card4}" height="100" rx="5" fill="white" filter="url(#shadow)"/>
        <text x="${positions.card4 + 10}" y="65" font-family="Inter, sans-serif" font-size="12" font-weight="bold" fill="url(#textGradient)">
          Units
        </text>
        ${createTextElement(unitsStr, positions.card4 + 10, 105, 36, widths.card4 - 20)}
      </svg>
    `;
    
    return svgContent;
  };

  const handleDownloadPDF = async () => {
    try {

      const hasDateFilters = filters.startDate && filters.endDate;
            
      let filteredDataForDownload = tableData;
      let dateRangeText = '';
      let propertyName = selectedPropertyName;
      
      if (!hasDateFilters) {
        dateRangeText = "All Time";
        
      } else {
        const startDateFormatted = formatDateToISO(filters.startDate, false).split('.')[0];
        const endDateFormatted = formatDateToISO(filters.endDate, true).split('.')[0];
        dateRangeText = `${startDateFormatted} to ${endDateFormatted}`;
        
      }

      if (filteredDataForDownload.length === 0) {
        showToast("warning", "No data available for the selected date range.");
        setIsPdfGenerating(false);
        return;
      }
      // Add progress indication for large datasets
      if (filteredDataForDownload.length > 500) {
        showToast("info", "Generating PDF for large dataset. Please wait...");
      }

      applyPlugin(jsPDF);
      const pdf = new jsPDF('p', 'mm', 'a4');

      let pageHeight = pdf.internal.pageSize.height;
      let pageWidth = pdf.internal.pageSize.width;

      let yPosition = 20;
      let xPosition = 20;
      
      // Contact fields 
      pdf.setFontSize(7);
      pdf.setTextColor(60, 60, 60);
      pdf.setFont("helvetica", "normal");
      
      pdf.text('Address:', xPosition, yPosition);
      pdf.text('TENAFIX LIMITED, P.O BOX 191810 KAMPALA UGANDA, CENTRAL, KAMPALA, KAWEMPE DIVISION, MULAGO 1, UPPER MAWANDA', xPosition + 15, yPosition + 0);

      pdf.text('Contact:', xPosition, yPosition + 5);
      pdf.text('+256 773 884476, +256 776 003035', xPosition + 15, yPosition + 5);

      pdf.text('Email:', xPosition, yPosition + 10);
      pdf.text('support@tenafix.co', xPosition + 15, yPosition + 10);

      pdf.setDrawColor(200, 200, 200);
      pdf.setLineWidth(0.2);
      pdf.line(xPosition, yPosition + 12, pageWidth - 20, yPosition + 12);

      yPosition += 20;

      pdf.setFontSize(16);
      pdf.setTextColor(0, 0, 0);
      pdf.setFont("helvetica", "bold");
      pdf.text('Occupancy Insights Report', 20, yPosition);

      yPosition += 10;

      // Report metadata
      pdf.setFontSize(10);
      pdf.setTextColor(60, 60, 60);
      pdf.setFont("helvetica", "normal");
      pdf.text('Reporting Period:', 20, yPosition);

      pdf.setTextColor(0, 0, 0);
      pdf.text(dateRangeText, 50, yPosition);
      yPosition += 7;

      pdf.setTextColor(60, 60, 60);
      pdf.text('Property:', 20, yPosition);
      pdf.setTextColor(0, 0, 0);
      pdf.text(propertyName, 50, yPosition);
      yPosition += 7;

      pdf.setTextColor(60, 60, 60);
      pdf.text('Generated on:', 20, yPosition);
      pdf.setTextColor(0, 0, 0);
      const now = new Date();
      const generationTime = `${now.toLocaleDateString()} at ${now.toLocaleTimeString()}`;
      pdf.text(generationTime, 50, yPosition);
      yPosition += 15;

      // Applied filters section
      pdf.setFontSize(12);
      pdf.setTextColor(0, 0, 0);
      pdf.setFont("helvetica", "bold");
      pdf.text('Applied Filters', 20, yPosition);
      yPosition += 8;

      pdf.setFontSize(9);
      pdf.setTextColor(80, 80, 80);
      pdf.setFont("helvetica", "normal");
      
      if (propertyName) {
        pdf.text(`• Property Filter: ${propertyName}`, 25, yPosition);
        yPosition += 5;
      }
      
      pdf.text(`• Date Range: ${dateRangeText}`, 25, yPosition);
      yPosition += 10;

      // Create SVG InfoCard with gradients
      const svgContent = createInfoCardSVG({
        rentCollected: aggregated.rentCollected,
        rentExpected: aggregated.rentExpected,
        propertyCount: propertyCount,
        totalUnits: aggregated.totalUnits
      });
      
      const infoCardCanvas = await svgToCanvas(svgContent, 800, 200); 
      const infoCardImgData = infoCardCanvas.toDataURL('image/png');
      const infoCardWidth = 170; // Full width within margins
      const infoCardHeight = (200 * infoCardWidth) / 800; // Proportional height calculation

      pdf.addImage(infoCardImgData, 'PNG', 20, yPosition, infoCardWidth, infoCardHeight);
      yPosition += infoCardHeight;

      // Capture chart as image
      let chartHeight = 0;
      if (chartRef.current) {
        const chartCanvas = await html2canvas(chartRef.current, {
          backgroundColor: '#ffffff',
          scale: 2, // Higher quality
          useCORS: true
        });
        
        const chartImgData = chartCanvas.toDataURL('image/png');
        const chartWidth = 80; // Width in mm
        chartHeight = (chartCanvas.height * chartWidth) / chartCanvas.width;

        pdf.addImage(chartImgData, 'PNG', 20, yPosition, chartWidth, chartHeight);
        yPosition += chartHeight;
      }

      // Capture legend as image
      if (legendRef.current) {
        const legendCanvas = await html2canvas(legendRef.current, {
          backgroundColor: '#ffffff',
          scale: 2,
          useCORS: true
        });
        
        const legendImgData = legendCanvas.toDataURL('image/png');
        const legendWidth = 60;
        const legendHeight = (legendCanvas.height * legendWidth) / legendCanvas.width;
        
        pdf.addImage(legendImgData, 'PNG', 110, yPosition - chartHeight, legendWidth, legendHeight);
      }

      // Add vacancy duration table
      const autoTableData = filteredDataForDownload.map(item => [
        item.unitId,
        item.propertyName,
        item.vacantDuration.toString()
      ]);

      const tableHeaders = ['Unit ID', 'Property Name', 'Days Vacant'];

      pdf.autoTable({
        head: [tableHeaders],
        body: autoTableData,
        startY: yPosition + 10,
        theme: 'grid',
        headStyles: {
          fillColor: [71, 114, 241],
          textColor: [255, 255, 255],
          fontSize: 10,
          fontStyle: 'bold',
          halign: 'center'
        },
        bodyStyles: {
          fontSize: 9,
          textColor: [0, 0, 0],
          cellPadding: 3
        },
        alternateRowStyles: {
          fillColor: [248, 249, 250] // Light gray for alternate rows
        },
        columnStyles: {
          0: { halign: 'center' },
          1: { halign: 'left' },
          2: { halign: 'center' }
        },
        margin: { left: 20, right: 20, top: 20, bottom: 20 },
        styles: {
          overflow: 'linebreak',
          fontSize: 9
        }
      });

      // Add header and footer to all pages
      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        
        const footerY = pageHeight - 15;

        // header section
        pdf.setFontSize(10);
        pdf.setTextColor(71, 114, 241); // Blue color for brand
        pdf.setFont("helvetica", "bold");
        pdf.text('Tenafix Limited', 20, 10);
        
        pdf.setFontSize(8);
        pdf.setTextColor(100, 100, 100);
        pdf.setFont("helvetica", "normal");
        pdf.text('Real Estate Management Software', 20, 14);
        
        pdf.addImage(Logo, 'PNG', pageWidth - 40, -5, 30, 30);
        
        // Footer section
        pdf.setFontSize(8);
        pdf.setTextColor(71, 114, 241);
        pdf.setFont("helvetica", "bold");
        pdf.text('Tenafix Limited', 20, footerY + 3);
        
        pdf.setTextColor(100, 100, 100);
        pdf.setFont("helvetica", "normal");
        pdf.text('|', 42, footerY + 3);
        pdf.text('www.tenafix.co', 44, footerY + 3);
        pdf.text('|', 64, footerY + 3);
        pdf.text('support@tenafix.co', 66, footerY + 3);
        
        // System generated note
        pdf.setFontSize(7);
        pdf.setTextColor(120, 120, 120);
        pdf.text('This is a system-generated report.', 20, footerY + 6);
        pdf.text('For assistance contact support@tenafix.co', 20, footerY + 9);
        
        // Page number (right aligned)
        pdf.setFontSize(8);
        pdf.setTextColor(100, 100, 100);
        const pageText = `Page ${i} of ${pageCount}`;
        const pageTextWidth = pdf.getTextWidth(pageText);
        pdf.text(pageText, pdf.internal.pageSize.width - pageTextWidth - 20, footerY + 8);
      }

      // Save PDF
      const localNow = new Date().toLocaleString().split(', ');
      const currentDate = `${localNow[0]}/${localNow[1].split(' ')[0]}`.replace(/[/:]/g, '-');
      pdf.save(`occupancy-insights-report-${currentDate}.pdf`);
      
    } catch (error) {
      showToast("error", "Failed to generate PDF. Please try again.");
    } finally {
      setIsPdfGenerating(false);
    }
  };

  const handleDownloadClick = () => {
    setIsPdfGenerating(true);
    setTimeout(() => handleDownloadPDF(), 0);
  }

  return (
    <div className="p-4 sm:p-6 md:p-[30px]">
      <div className="">
        <TableHeader
          searchValue={filters.propertyId}
          onSearchSubmit={handleSearch}
          filters={[
            {
              name: "Filter by Property",
              value: filters.dropdownOptions,
              onChange: (val) =>
                setFilters((prev) => ({ ...prev, propertyId: val })),
              options: [
                ...(dropdownOptions.length > 0 &&
                  !dropdownOptions[0].disabled
                    ? [{ label: "All properties", value: "" }]
                    : []),
                ...dropdownOptions,
              ],
            },
          ]}
          datePicker={{
            startDate: filters.startDate,
            endDate: filters.endDate,
            onChange: (field, value) =>
              setFilters((prev) => ({ ...prev, [field]: value })),
          }}
          buttons={[
            {
              label: isLoading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                "SEARCH"
              ),
              onClick: handleSearch,
              loading: isLoading,
              disabled: isLoading,
              variant: "main",
              className:
                "bg-[#9333EA] text-white h-[36px] sm:h-[39px] md:h-[42px] lg:h-[45px] px-3 rounded-[3.77px] uppercase w-[102px] flex items-center justify-center",
            },
            {
              label: "DOWNLOAD REPORT",
              onClick: handleDownloadClick,
              loading: isPdfGenerating || isLoading,
              disabled: isPdfGenerating || isLoading || !filteredData.length,
              variant: "primary",
              className:
                "bg-[#C084FC] text-white h-[36px] sm:h-[39px] md:h-[42px] lg:h-[45px] px-4 rounded-[4px] w-[170px] uppercase font-bold",
            }
          ]}
          datePickerClass="sm:w-full md:w-full lg:w-full flex flex-grow w-full"
          buttonWrapperClass="sm:mr-0 mx-auto w-full flex flex-row [@media(min-width:360px)]:w-full   [@media(min-width:360px)]:flex [@media(min-width:360px)]:flex-row justify-center items-center [@media(min-width:640px)]:justify-start [@media(min-width:768px)]:justify-end lg:justify-end"
          containerClass="md:grid lg:grid-cols-3 md:grid-cols-3 flex flex-wrap lg:flex-nowrap xl:grid xl:grid-rows-1"  //[@media(min-width:1516px)]:
          dropdownWrapperClass="sm:max-w-full md:max-w-full flex flex-grow w-full"
        />
      </div>

      {/* InfoCard Section */}
      <div className="my-4 sm:my-6 md:my-[30px] p-3 sm:p-5 md:p-6 lg:p-[30px] bg-white px-[30px] py-[31px] rounded-[10px] shadow-[0_4px_4px_0_#0000000F]">
        <div
          className="grid gap-x-6 sm:gap-x-8 md:gap-x-10 lg:gap-x-[52px] gap-y-[30px] grid-cols-2 xl:grid-cols-4 md:grid-cols-2 lg:grid-cols-3"
        >
          <div>
            <InfoCard
              label="Rent Collected (UGX)"
              value={aggregated.rentCollected.toFixed(2)}
              className=" max-h-[120px] mx-auto overflow-hidden"
              labelStyle="font-inter font-semibold text-[14px] leading-[100%] bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent mb-5"
              valueStyle="font-inter font-semibold text-[25px] lg:text-[29px] leading-[100%] tracking-normal bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
              popoverTextStyle="text-level-4"
            />
          </div>
          <div>
            <InfoCard
              label="Rent Expected (UGX)"
              value={aggregated.rentExpected.toFixed(2)}
              className=" max-h-[120px] mx-auto overflow-hidden"
              labelStyle="font-inter font-semibold text-[14px] leading-[100%] bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent mb-5"
              valueStyle="font-inter font-semibold text-[25px] lg:text-[29px] leading-[100%] tracking-normal bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
              popoverTextStyle="text-level-4"
            />
          </div>
          <div>
            <InfoCard
              label="Properties"
            //  value={propertyCount}
              value={propertiesCount}
              className=" max-h-[100px] mx-auto"
              labelStyle="font-inter font-semibold text-[14px] leading-[100%] bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
              valueStyle="font-inter font-semibold text-[25px] lg:text-[50px] leading-[100%] tracking-normal bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
              popoverTextStyle="text-level-4"
            />
          </div>
          <div>
            <InfoCard
              label="Units"
              value={aggregated.totalUnits}
              className=" max-h-[100px] mx-auto"
              labelStyle="font-inter font-semibold text-[14px] leading-[100%] bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
              valueStyle="font-inter font-semibold text-[25px] lg:text-[50px] leading-[100%] tracking-normal bg-gradient-to-b from-[#4E6CF0] to-[#8442E3] bg-clip-text text-transparent"
              popoverTextStyle="text-level-4"
            />
          </div>
        </div>
      </div>

      {/* Donut Chart */}
      <div className="bg-white rounded-[10px] p-4 sm:p-5 md:p-6 lg:p-[30px] mt-4 sm:mt-6 md:mt-[30px]">
        <div className="font-semibold text-blue font-inter text-level-5 sm:text-xl md:text-level-4 mb-4 sm:mb-6 md:mb-[30px]">
          Manage Properties
        </div>

        <div className="flex flex-col items-center justify-between md:flex-row gap-6 lg:gap-2 xl:gap-6">
          <div  
            ref={chartRef}
            className="rounded-[5px] w-full md:w-[60%] [@media(min-width:456px)]:border-[2px] border-[#F9F9F9] p-0 sm:p-2 z-0 ov xl:p-[30px]  [@media(min-width:456px)]:shadow-box-9696">
            <OccupancyRateChart data={formattedChartData}
          />
          </div>
          <div 
            ref={legendRef}
            className="rounded-[5px] w-full md:w-[40%] border-[2px] border-[#F9F9F9] p-2 xl:p-[30px]   shadow-box-9696">
              <LegendTable
                data={legendData}
                showDot={true}
                className="mt-4"
                classNameLabel="text-[14px] font-[400] text-[#404040] leading-5"
                classNameValue="text-[14px] font-[500] text-[#0A0A0A] leading-5"
                classNamePercent="text-[14px] font-[500] text-[#0A0A0A] leading-5"
              />
          </div>
        </div>
      </div>

      <div>
        <div className="font-semibold text-blue font-inter text-level-5 sm:text-xl md:text-level-4 my-4 sm:my-6 md:my-[30px]">
          Vacancy Duration Table
        </div>

        <div>
          <Table
            dataSource={paginatedTableData}
            columns={columns}
            rowKey="unitId"
            hasSubTable={false}
          />
          <div className="flex justify-center mt-4">
            <Pagination
              current={currentPage}
              total={tableData.length}
              pageSize={ITEMS_PER_PAGE}
              onChange={handlePageChange}
              className="custom-pagination"
              showSizeChanger={false}
              hideOnSinglePage={true}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default OccupancyRate;
